// Revenue projections: what is real, kept visibly apart from what is estimated.
//
// The rows are `GET /api/reports/projection`. Three kinds of money, and the
// screen's whole job is to never let one pass for another:
//
//   ACTUAL      issued invoices — money on documents.
//   CONTRACTED  contract terms on file that no document has issued yet.
//   ESTIMATE    deals with no contract, from their own estimate fields and the
//               scenario's assumptions. Never real money.
//
// So actual and contracted are drawn solid and the estimate hatched, every
// estimate tile is dashed and badged, and the monthly table splits its columns
// under two headings. Deals themselves are on the deals screens; this page is
// the totals.
//
// The SCENARIO (go-live capacity, growth, margin, the assumed share, the
// implementation timing) starts from the saved assumptions. Editing it is a
// what-if for this reading only — it lives in the URL — until an editor saves
// it as the default.

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Currency } from "@crm/shared";
import type {
  ClosingPace,
  Partner,
  ProjectionAssumptions,
  ProjectionReport,
  ProjectionSettings,
  ReportScope,
} from "@crm/shared";
import styles from "./ProjectionsPage.module.css";
import {
  ASSUMPTION_FIELDS,
  ASSUMPTION_KEYS,
  MEASURE_LABEL,
  chartCaveats,
  chartSeries,
  effectiveAssumptions,
  hasOverrides,
  meaningfulOverrides,
  parseAssumption,
  schedulingSentence,
  settingsUpdateFor,
  windowFigures,
  type AssumptionKey,
  type Measure,
  type ProjectionQueryState,
} from "./projectionModel.js";
import {
  projectionStateToSearch,
  searchToProjectionState,
  type ProjectionSearch,
} from "./projectionSearch.js";
import { MonthlyChart } from "../../design/chart/MonthlyChart.js";
import { Badge } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardBody, CardHeader } from "../../design/Card.js";
import { Notice, ProblemMessage } from "../../design/Feedback.js";
import { FxFootnote } from "../../design/FxFootnote.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { formatMonth } from "../../format/calendarDate.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { useDebouncedValue } from "../../lib/useDebouncedValue.js";
import { formatMoney, formatPercent, formatQuantity } from "../../format/money.js";
import { isApiProblem } from "../../api/problem.js";
import { useUpdateProjectionSettingsMutation } from "../../api/mutations.js";
import {
  useClosingPace,
  usePartnerOptions,
  useProjectionReport,
  useProjectionSettings,
} from "../../api/queries.js";
import { useHasRole } from "../../auth/useAuth.js";

const ANY_PARTNER = "__any__";

const SCOPES: ReadonlyArray<SegmentedOption<ReportScope>> = [
  { value: "direct", label: "This company" },
  { value: "rolled_up", label: "Including owned companies" },
];

const pct1 = (value: number) => formatPercent(value, 1);

export function ProjectionsPage(): ReactNode {
  const search = useSearch({ from: "/projections" });
  const navigate = useNavigate();
  const reportCurrency = useReportCurrency();
  const state = useMemo(
    () => searchToProjectionState(search, reportCurrency),
    [search, reportCurrency],
  );
  // Always OUR share, after every partner's: what we earn is the figure that
  // matters, and a gross switch here only invited reading the wrong one.
  const measure: Measure = "our_share";

  const setState = useCallback(
    (next: ProjectionQueryState) => {
      void navigate({
        to: "/projections",
        search: projectionStateToSearch(next) as ProjectionSearch,
      });
    },
    [navigate],
  );

  // A month field fires on every keystroke of a typed date; the report follows
  // once the window holds still.
  const report = useProjectionReport(useDebouncedValue(state));
  const settings = useProjectionSettings();
  const partners = usePartnerOptions();

  usePageChrome({ title: "Projections" });

  return (
    <div className={styles.page}>
      <HeadlineTiles report={report.data} measure={measure} />

      <div className={styles.toolbar}>
        <PartnerFilter
          partners={partners.data?.items ?? []}
          partnerId={state.partnerId}
          onChange={(partnerId) => setState({ ...state, partnerId })}
        />
        {state.partnerId !== null && (
          <Segmented
            label="Partner scope"
            variant="inset"
            options={SCOPES}
            value={state.scope}
            onChange={(scope) => setState({ ...state, scope })}
          />
        )}
        <MonthInput
          label="From"
          value={state.fromMonth}
          placeholder={report.data?.params.from_month}
          onChange={(fromMonth) => setState({ ...state, fromMonth })}
        />
        <MonthInput
          label="To"
          value={state.toMonth}
          placeholder={report.data?.params.to_month}
          onChange={(toMonth) => setState({ ...state, toMonth })}
        />
      </div>

      <ScenarioPanel
        state={state}
        settings={settings.data ?? null}
        settingsError={settings.error}
        report={report.data}
        onChange={setState}
        onRefetchSettings={() => void settings.refetch()}
      />

      <QueryBoundary query={report} label="the projection">
        {(data) => <ProjectionBody report={data} measure={measure} />}
      </QueryBoundary>
    </div>
  );
}

function PartnerFilter({
  partners,
  partnerId,
  onChange,
}: {
  partners: readonly Partner[];
  partnerId: string | null;
  onChange: (partnerId: string | null) => void;
}): ReactNode {
  return (
    <select
      className={styles.control}
      aria-label="Partner"
      value={partnerId ?? ANY_PARTNER}
      onChange={(event) => {
        const picked = event.target.value;
        onChange(picked === ANY_PARTNER ? null : picked);
      }}
    >
      <option value={ANY_PARTNER}>Every partner</option>
      {partners.map((partner) => (
        <option key={partner.id} value={partner.id}>
          {partner.name}
        </option>
      ))}
    </select>
  );
}

function MonthInput({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string | null;
  /** The month the report chose when this end is left to it. */
  placeholder: string | undefined;
  onChange: (value: string | null) => void;
}): ReactNode {
  return (
    <label className={styles.monthControl}>
      <span className={styles.controlLabel}>{label}</span>
      <input
        type="month"
        className={styles.control}
        aria-label={`Window ${label.toLowerCase()}`}
        value={value ?? placeholder ?? ""}
        onChange={(event) => onChange(event.target.value === "" ? null : event.target.value)}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------------

function KindBadge({ kind }: { kind: "actual" | "contracted" | "estimate" }): ReactNode {
  switch (kind) {
    case "actual":
      return (
        <Badge shape="tag" tone="good">
          Actual
        </Badge>
      );
    case "contracted":
      return (
        <Badge shape="tag" tone="teal">
          Contracted
        </Badge>
      );
    case "estimate":
      return (
        <Badge shape="tag" tone="warn">
          Estimate
        </Badge>
      );
  }
}

/**
 * The window's figures. Real money first, estimates after and drawn as
 * estimates. The weighted and unweighted estimates sit side by side so the
 * reader sees how much of the figure is the win probability.
 */
function HeadlineTiles({
  report,
  measure,
}: {
  report: ProjectionReport | undefined;
  measure: Measure;
}): ReactNode {
  const figures = report === undefined ? null : windowFigures(report, measure);
  const money = (minor: number) =>
    report === undefined ? null : formatMoney(minor, report.currency);
  const window =
    report === undefined
      ? "over the window"
      : `${formatMonth(report.params.from_month)} – ${formatMonth(report.params.to_month)}`;
  const measureWord = MEASURE_LABEL[measure].toLowerCase();

  return (
    <div className={styles.tiles}>
      <StatTileRow>
        <StatTile
          label="Real revenue"
          value={figures === null ? null : money(figures.real)}
          hint={
            figures === null
              ? `Invoiced and contracted, ${measureWord}`
              : `${money(figures.actual)} invoiced + ${money(figures.contracted)} contracted, ${measureWord} · ${window}${
                  figures.unpricedLineCount === 0
                    ? ""
                    : ` · ${figures.unpricedLineCount} metered line-months not yet priced`
                }`
          }
        />
        <StatTile
          label="Estimated revenue"
          variant="estimate"
          badge={<KindBadge kind="estimate" />}
          value={figures === null ? null : money(figures.estimateWeighted)}
          hint={
            figures === null
              ? "Deals with no contract yet, × win probability"
              : `Deals with no contract yet, ${measureWord}, × win probability · ${money(figures.estimateUnweighted)} if all close`
          }
        />
        <StatTile
          label="Estimated margin"
          variant="estimate"
          badge={<KindBadge kind="estimate" />}
          value={figures === null ? null : money(figures.marginWeighted)}
          hint={
            report === undefined
              ? "Assumed margin on our share of the estimates"
              : `${pct1(report.params.assumptions.margin_pct)} assumed on our share of the estimates, × win probability`
          }
        />
        <StatTile
          label="Expected go-lives"
          variant="estimate"
          badge={<KindBadge kind="estimate" />}
          value={figures === null ? null : formatQuantity(roundTo(figures.expectedGoLives, 2))}
          hint="Deals going live in the window, each counted by its win probability"
        />
      </StatTileRow>
    </div>
  );
}

/** Display rounding for a count of expected go-lives, which is a sum of probabilities. */
function roundTo(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

// ---------------------------------------------------------------------------
// Scenario
// ---------------------------------------------------------------------------

type SaveOutcome =
  | { kind: "idle" }
  | { kind: "saved"; version: number }
  | { kind: "conflict" }
  | { kind: "failed"; error: unknown };

function ScenarioPanel({
  state,
  settings,
  settingsError,
  report,
  onChange,
  onRefetchSettings,
}: {
  state: ProjectionQueryState;
  settings: ProjectionSettings | null;
  settingsError: unknown;
  report: ProjectionReport | undefined;
  onChange: (state: ProjectionQueryState) => void;
  onRefetchSettings: () => void;
}): ReactNode {
  const canEdit = useHasRole("crm_editor");
  const save = useUpdateProjectionSettingsMutation();
  const pace = useClosingPace();
  const [outcome, setOutcome] = useState<SaveOutcome>({ kind: "idle" });

  // The saved assumptions with this reading's overrides on top. Until the
  // settings answer, the report says what it ran under.
  const effective: ProjectionAssumptions | null =
    settings !== null
      ? effectiveAssumptions(settings, state.overrides)
      : (report?.params.assumptions ?? null);
  const overridden = hasOverrides(state.overrides);

  const commit = (key: AssumptionKey, value: number) => {
    setOutcome({ kind: "idle" });
    onChange({
      ...state,
      overrides: meaningfulOverrides({ ...state.overrides, [key]: value }, settings),
    });
  };

  const reset = () => {
    setOutcome({ kind: "idle" });
    onChange({ ...state, overrides: {} });
  };

  /** The capacity and growth the closing pace suggests, as a scenario to look at before saving. */
  const applyPace = (suggested: { go_lives_per_month?: number; monthly_growth_pct?: number }) => {
    setOutcome({ kind: "idle" });
    onChange({
      ...state,
      overrides: meaningfulOverrides({ ...state.overrides, ...suggested }, settings),
    });
  };

  const saveAsDefault = () => {
    if (settings === null) return;
    const body = settingsUpdateFor(state.overrides, settings);
    save.mutate(
      { body, version: settings.version },
      {
        onSuccess: (saved) => {
          setOutcome({ kind: "saved", version: saved.version });
          // The scenario IS the default now, so it is no longer a what-if.
          onChange({ ...state, overrides: {} });
        },
        onError: (error) => {
          if (isApiProblem(error) && (error.status === 409 || error.code === "crm-conflict")) {
            setOutcome({ kind: "conflict" });
            onRefetchSettings();
          } else {
            setOutcome({ kind: "failed", error });
          }
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title="Scenario"
        description={
          <>
            {overridden
              ? "A what-if for this reading only — it lives in the link, and the saved assumptions are unchanged."
              : "The saved assumptions. Change any of them to see another scenario; nothing is saved unless you save it."}
            {settings !== null && (
              <span className={styles.version}> Saved assumptions v{settings.version}.</span>
            )}
            {report !== undefined &&
              settings !== null &&
              report.params.settings_version !== settings.version && (
                <span className={styles.version}>
                  {" "}
                  This reading started from v{report.params.settings_version}.
                </span>
              )}
          </>
        }
        actions={
          <div className={styles.scenarioActions}>
            <Button small variant="subtle" disabled={!overridden} onClick={reset}>
              Reset to saved
            </Button>
            {canEdit && (
              <Button
                small
                variant="primary"
                disabled={!overridden || settings === null || save.isPending}
                onClick={saveAsDefault}
              >
                {save.isPending ? "Saving…" : "Save as default"}
              </Button>
            )}
          </div>
        }
      />
      <CardBody>
        {settingsError !== null && settingsError !== undefined && (
          <ProblemMessage error={settingsError} action="load the saved assumptions" />
        )}
        {outcome.kind === "conflict" && (
          <Notice tone="warn" title="Someone changed the saved assumptions while you were looking.">
            They have been reloaded{settings === null ? "" : ` (now v${settings.version})`}. Your
            scenario is still applied — check it against theirs, and save again to replace them.
          </Notice>
        )}
        {outcome.kind === "failed" && (
          <ProblemMessage error={outcome.error} action="save the assumptions" />
        )}
        {outcome.kind === "saved" && (
          <div className={styles.saved} role="status">
            Saved as the default assumptions (v{outcome.version}).
          </div>
        )}
        {pace.data !== undefined && (
          <ClosingPaceLine pace={pace.data} current={effective} onUse={applyPace} />
        )}
        <div className={styles.scenarioGrid}>
          {ASSUMPTION_KEYS.map((key) => (
            <ScenarioInput
              // Remounted when the value it shows changes from outside (a reset,
              // a reload), so a half-typed draft never outlives the value it edited.
              key={`${key}:${effective?.[key] ?? ""}`}
              assumption={key}
              value={effective?.[key] ?? null}
              overridden={state.overrides[key] !== undefined}
              saved={settings?.[key] ?? null}
              onCommit={(value) => commit(key, value)}
            />
          ))}
        </div>
        {effective !== null && (
          <p className={styles.explain}>{schedulingSentence(effective, pct1)}</p>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * The pace deals have actually closed at, and the capacity and growth that
 * would match it: wins a month lately, the change on the half-year before, and
 * the last months' wins as bars.
 */
function ClosingPaceLine({
  pace,
  current,
  onUse,
}: {
  pace: ClosingPace;
  current: ProjectionAssumptions | null;
  onUse: (suggested: { go_lives_per_month?: number; monthly_growth_pct?: number }) => void;
}): ReactNode {
  const capacity = pace.suggested_go_lives_per_month;
  const growth = pace.suggested_monthly_growth_pct;
  if (capacity === null) return null;
  const suggested = {
    go_lives_per_month: capacity,
    ...(growth === null ? {} : { monthly_growth_pct: growth }),
  };
  const applied =
    current !== null &&
    current.go_lives_per_month === capacity &&
    (growth === null || current.monthly_growth_pct === growth);
  const max = Math.max(1, ...pace.months.map((month) => month.won_count));
  const wins = pace.months.reduce((sum, month) => sum + month.won_count, 0);
  return (
    <div className={styles.pace} role="group" aria-label="Closing pace">
      <div
        className={styles.paceBars}
        role="img"
        aria-label={`${wins} wins in ${pace.months.length} months`}
      >
        {pace.months.map((month) => (
          <span
            key={month.month}
            title={`${formatMonth(month.month)}: ${month.won_count} won`}
            style={{ height: `${Math.max(2, (month.won_count / max) * 100)}%` }}
            className={month.won_count === 0 ? styles.paceBarEmpty : styles.paceBar}
          />
        ))}
      </div>
      <div className={styles.paceText}>
        <span className={styles.paceFigure}>{`${capacity} wins a month lately`}</span>
        <span>
          {growth === null
            ? ` · ${wins} in ${pace.months.length} months`
            : ` · ${growth >= 0 ? "+" : "−"}${Math.abs(growth)}% a month · ${wins} in ${pace.months.length} months`}
        </span>
      </div>
      <Button small variant="subtle" disabled={applied} onClick={() => onUse(suggested)}>
        {applied ? "In use" : "Use these"}
      </Button>
    </div>
  );
}

/**
 * One assumption. Text until it is committed — on blur or Enter — so typing
 * "2.5" does not fire a report for "2." on the way, and a value the report
 * would refuse is said here rather than sent.
 */
function ScenarioInput({
  assumption,
  value,
  overridden,
  saved,
  onCommit,
}: {
  assumption: AssumptionKey;
  value: number | null;
  overridden: boolean;
  saved: number | null;
  onCommit: (value: number) => void;
}): ReactNode {
  const field = ASSUMPTION_FIELDS[assumption];
  const [text, setText] = useState(value === null ? "" : String(value));
  const [error, setError] = useState<string | null>(null);
  const inputId = `scenario-${assumption}`;

  const commit = () => {
    if (value !== null && text.trim() === String(value)) {
      setError(null);
      return;
    }
    const parsed = parseAssumption(assumption, text);
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }
    setError(null);
    if (parsed.value !== value) onCommit(parsed.value);
  };

  return (
    <div className={`${styles.assumption} ${overridden ? styles.overridden : ""}`}>
      <label className={styles.assumptionLabel} htmlFor={inputId}>
        {field.label}
      </label>
      <span className={styles.assumptionControl}>
        <input
          id={inputId}
          type="text"
          inputMode="decimal"
          className={styles.control}
          value={text}
          aria-invalid={error !== null}
          aria-describedby={`${inputId}-hint`}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit();
          }}
        />
        <span className={styles.suffix}>{field.suffix}</span>
      </span>
      <span
        id={`${inputId}-hint`}
        className={error === null ? styles.assumptionHint : styles.assumptionError}
      >
        {error ?? (overridden && saved !== null ? `What-if · saved: ${saved}` : field.hint)}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chart and tables
// ---------------------------------------------------------------------------

/** Axis ticks for money: whole units, no cents, no code — the card says the currency once. */
function tickMoney(minor: number): string {
  const major = Math.trunc(minor / 100);
  const abs = Math.abs(major);
  const sign = major < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${sign}$${abs}`;
}

function ProjectionBody({
  report,
  measure,
}: {
  report: ProjectionReport;
  measure: Measure;
}): ReactNode {
  const months = report.rows.map((row) => row.month);
  const money = (minor: number) => formatMoney(minor, report.currency, { omitCurrencyCode: true });

  return (
    <>
      <Card>
        <CardHeader
          title="Month by month"
          description={`${MEASURE_LABEL[measure]}, in ${report.currency}. Actual and contracted are solid; the estimate is hatched on top, weighted by win probability. A bar's full height combines real money with an estimate — read the segments, not the total.`}
        />
        <CardBody>
          <MonthlyChart
            label="Projected revenue by month"
            months={months}
            series={chartSeries(report.rows, measure)}
            formatValue={money}
            formatTick={tickMoney}
            caveats={chartCaveats(report.rows)}
            stacked
          />
        </CardBody>
      </Card>

      <details className={styles.monthDetails}>
        <summary>Show monthly figures</summary>
        <MonthTable report={report} measure={measure} />
      </details>

      <div className={styles.footer}>
        <FxFootnote currency={report.currency} fx={report.fx} generatedAt={report.generated_at} />
      </div>
    </>
  );
}

function MonthTable({
  report,
  measure,
}: {
  report: ProjectionReport;
  measure: Measure;
}): ReactNode {
  const money = (minor: number) => formatMoney(minor, report.currency, { omitCurrencyCode: true });
  const actualOf = measure === "gross" ? "gross_minor" : "our_share_minor";
  const est = styles.estimateCell;
  const unpriced = (n: number) =>
    n === 0 ? null : (
      <span
        className={styles.note}
        title="Metered lines a future month cannot price yet — left out, never projected as zero."
      >
        {" "}
        ({n} unpriced)
      </span>
    );
  const totals = report.totals;
  return (
    <Table label="Projection by month">
      <thead>
        <tr>
          <Th colSpan={3}>Schedule</Th>
          <Th colSpan={2} className={styles.groupReal}>
            Real money ({MEASURE_LABEL[measure].toLowerCase()})
          </Th>
          <Th colSpan={5} className={`${styles.groupEstimate} ${est}`}>
            Estimate — no contract
          </Th>
        </tr>
        <tr>
          <Th>Month</Th>
          <Th numeric>Capacity</Th>
          <Th numeric>Expected go-lives</Th>
          <Th numeric>Actual</Th>
          <Th numeric>Contracted</Th>
          <Th numeric className={est}>
            Implementation
          </Th>
          <Th numeric className={est}>
            Recurring
          </Th>
          <Th numeric className={est}>
            Our share
          </Th>
          <Th numeric className={est}>
            Our share, weighted
          </Th>
          <Th numeric className={est}>
            Margin, weighted
          </Th>
        </tr>
      </thead>
      <tbody>
        {report.rows.length === 0 ? (
          <EmptyRow colSpan={10} title="No months in this window" description="Widen the window." />
        ) : (
          report.rows.map((row) => (
            <tr key={row.month}>
              <Td mono nowrap>
                {formatMonth(row.month)}
              </Td>
              <Td numeric mono>
                {formatQuantity(roundTo(row.go_live_capacity, 2))}
              </Td>
              <Td numeric mono>
                {formatQuantity(roundTo(row.expected_go_lives, 2))}
              </Td>
              <Td numeric mono>
                {money(row.actual[actualOf])}
              </Td>
              <Td numeric mono>
                {money(row.contracted[actualOf])}
                {unpriced(row.contracted.unpriced_line_count)}
              </Td>
              <Td numeric mono className={est}>
                {money(row.estimate.implementation_gross_minor)}
              </Td>
              <Td numeric mono className={est}>
                {money(row.estimate.recurring_gross_minor)}
              </Td>
              <Td numeric mono className={est}>
                {money(row.estimate.our_share_minor)}
              </Td>
              <Td numeric mono className={est}>
                {money(row.estimate.weighted_our_share_minor)}
              </Td>
              <Td numeric mono className={est}>
                {money(row.estimate.weighted_margin_minor)}
              </Td>
            </tr>
          ))
        )}
      </tbody>
      <tfoot>
        <tr>
          <Td strong>Total</Td>
          {/* Capacity is a level per month; a sum of it means nothing. */}
          <Td numeric muted>
            —
          </Td>
          <Td numeric mono strong>
            {formatQuantity(roundTo(totals.expected_go_lives, 2))}
          </Td>
          <Td numeric mono strong>
            {money(totals.actual[actualOf])}
          </Td>
          <Td numeric mono strong>
            {money(totals.contracted[actualOf])}
            {unpriced(totals.contracted.unpriced_line_count)}
          </Td>
          <Td numeric mono strong className={est}>
            {money(totals.estimate.implementation_gross_minor)}
          </Td>
          <Td numeric mono strong className={est}>
            {money(totals.estimate.recurring_gross_minor)}
          </Td>
          <Td numeric mono strong className={est}>
            {money(totals.estimate.our_share_minor)}
          </Td>
          <Td numeric mono strong className={est}>
            {money(totals.estimate.weighted_our_share_minor)}
          </Td>
          <Td numeric mono strong className={est}>
            {money(totals.estimate.weighted_margin_minor)}
          </Td>
        </tr>
      </tfoot>
    </Table>
  );
}
